import { useEffect, useState } from "react";
import { fetchListing } from "../api";
import { bedLabel, cap, fmtDate, money, num } from "../format";
import { ListingCard, Photo } from "./ListingCard";
import { ListingMap } from "./ListingMap";
import type { BuildingFacts, ListingDetail } from "../../../shared/types";

const cache = new Map<string, ListingDetail>();

export function DetailPage({
  id,
  onOpen,
  onBack,
  onAsk,
}: {
  id: string;
  onOpen: (id: string) => void;
  onBack: () => void;
  onAsk: (question: string) => void;
}) {
  const [data, setData] = useState<ListingDetail | null>(() => cache.get(id) ?? null);
  const [state, setState] = useState<"ready" | "loading" | "missing" | "error">(
    cache.has(id) ? "ready" : "loading",
  );
  const [shot, setShot] = useState(0);

  useEffect(() => {
    setShot(0);
    const cached = cache.get(id);
    if (cached) {
      setData(cached);
      setState("ready");
      return;
    }

    let cancelled = false;
    setState("loading");

    fetchListing(id)
      .then((result) => {
        if (cancelled) return;
        if (!result) return setState("missing");
        cache.set(id, result);
        setData(result);
        setState("ready");
      })
      .catch(() => !cancelled && setState("error"));

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state === "loading") {
    return (
      <main className="detail-page">
        <div className="listing-state">
          <span className="spinner" />
          Loading listing…
        </div>
      </main>
    );
  }

  if (state !== "ready" || !data) {
    return (
      <main className="detail-page">
        <div className="listing-state">
          {state === "missing" ? `No listing called "${id}".` : "Couldn't load this listing."}
          <button className="ghost-btn" type="button" onClick={onBack}>
            Back to chat
          </button>
        </div>
      </main>
    );
  }

  const l = data.listing;

  return (
    <main className="detail-page" key={id}>
      <article className="listing">
        <button className="back-link" type="button" onClick={onBack}>
          ← Back to chat
        </button>

        <div className="gallery">
          <div className="gallery-main">
            <Photo id={l.images[shot]} width={1400} alt={l.title} key={l.images[shot]} />
          </div>
          <div className="gallery-thumbs">
            {l.images.map((image, i) => (
              <button
                className={"thumb" + (i === shot ? " active" : "")}
                type="button"
                key={image}
                aria-label={`Photo ${i + 1} of ${l.images.length}`}
                onClick={() => setShot(i)}
              >
                <Photo id={image} width={320} alt="" />
              </button>
            ))}
          </div>
        </div>

        <header className="listing-head">
          <h1>{l.title}</h1>
          <div className="listing-sub">
            {l.address ? `${l.address} · ` : ""}
            {l.neighborhood}, {l.city}, {l.province}
          </div>
          <div className="score-row">
            <span className="score-pill">
              <strong>{l.walkScore}</strong> Walk Score
            </span>
            <span className="score-pill">
              <strong>{l.transitScore}</strong> Transit Score
            </span>
          </div>
        </header>

        <div className="spec-strip">
          <Spec value={bedLabel(l.beds)} label={l.beds === 0 ? "layout" : "bedrooms"} />
          <Spec value={String(l.baths)} label={l.baths === 1 ? "bathroom" : "bathrooms"} />
          <Spec value={num(l.sqft)} label="square feet" />
          <Spec value={cap(l.propertyType)} label="property type" />
        </div>

        <div className="listing-grid">
          <div className="listing-main">
            <section className="listing-section">
              <h2>About this place</h2>
              <p className="listing-about">{l.description}</p>
            </section>

            {l.building && <BuildingSection facts={l.building} />}

            <section className="listing-section">
              <h2>Amenities</h2>
              <ul className="amenity-list">
                {l.amenities.map((a) => (
                  <li key={a}>{cap(a)}</li>
                ))}
              </ul>
            </section>

            <section className="listing-section">
              <h2>Location</h2>
              <ListingMap listing={l} />
            </section>
          </div>

          <aside className="listing-aside">
            <div className="aside-card">
              <div className="aside-price">
                {money(l.price)}
                <span>/mo CAD</span>
              </div>

              <dl className="aside-facts">
                <Fact k="Available" v={fmtDate(l.availableFrom)} />
                <Fact k="Minimum lease" v={`${l.minLeaseMonths} months`} />
                <Fact k="Parking" v={cap(l.parking)} />
                <Fact k="Furnished" v={l.furnished ? "Yes" : "No"} />
                <Fact k="Pets" v={l.petsAllowed ? "Allowed" : "Not allowed"} />
              </dl>

              <button
                className="primary-btn"
                type="button"
                onClick={() => onAsk(`Tell me more about the ${l.title}. What's the catch?`)}
              >
                Ask Loop about this place
              </button>
              <button className="ghost-btn wide" type="button" onClick={onBack}>
                Back to results
              </button>
            </div>
          </aside>
        </div>

        {data.similar.length > 0 && (
          <section className="similar">
            <h2>Similar places</h2>
            <div className="cards">
              {data.similar.map((s, i) => (
                <ListingCard listing={s} index={i} key={s.id} onOpen={onOpen} />
              ))}
            </div>
          </section>
        )}
      </article>
    </main>
  );
}

/**
 * Building-level facts, straight from the City of Toronto apartment registry.
 * Only shown for listings in real registered buildings, and only for fields the
 * City actually recorded. A missing value is left out rather than guessed at.
 */
function BuildingSection({ facts }: { facts: BuildingFacts }) {
  const yn = (v: boolean | null) => (v === null ? null : v ? "Yes" : "No");
  const title = (s: string | null) => (s ? s.charAt(0) + s.slice(1).toLowerCase() : null);

  const rows: [string, string | null][] = [
    ["Address", facts.address],
    ["Built", facts.yearBuilt ? String(facts.yearBuilt) : null],
    ["Storeys", facts.storeys ? String(facts.storeys) : null],
    ["Units in building", facts.units ? num(facts.units) : null],
    [
      "Elevators",
      facts.elevators !== null
        ? `${facts.elevators}${facts.unitsPerElevator ? ` · ~${facts.unitsPerElevator} units each` : ""}`
        : null,
    ],
    ["Heating", title(facts.heatingType)],
    ["Heat included", yn(facts.heatIncluded)],
    ["Hydro included", yn(facts.hydroIncluded)],
    ["Water included", yn(facts.waterIncluded)],
    ["Air conditioning", facts.coolingType === "NONE" ? "None" : title(facts.coolingType)],
    ["Visitor parking", title(facts.visitorParking)],
    ["Garbage chute", yn(facts.garbageChute)],
    ["Locker room", yn(facts.lockerRoom)],
    ["Shared laundry", yn(facts.sharedLaundryRoom)],
    ["Indoor bike spaces", facts.indoorBikeSpaces ? num(facts.indoorBikeSpaces) : null],
    ["Barrier-free entrance", yn(facts.barrierFreeEntrance)],
    ["Non-smoking building", yn(facts.nonSmoking)],
    ["City evaluation score", facts.safetyScore !== null ? `${facts.safetyScore}/100` : null],
  ];

  const present = rows.filter(([, v]) => v !== null) as [string, string][];

  return (
    <section className="listing-section">
      <h2>The building</h2>
      <dl className="building-facts">
        {present.map(([k, v]) => (
          <div className="building-fact" key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      <p className="map-note">
        Building details from the City of Toronto apartment building registry. Contains information licensed
        under the Open Government Licence – Toronto.
      </p>
    </section>
  );
}

function Spec({ value, label }: { value: string; label: string }) {
  return (
    <div className="spec">
      <div className="spec-value">{value}</div>
      <div className="spec-label">{label}</div>
    </div>
  );
}

function Fact({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt>{k}</dt>
      <dd>{v}</dd>
    </>
  );
}
